<template>
  <ion-page>
    <ion-header :translucent="true">
      <ion-toolbar>
        <ion-title>EyeGaze Chess</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content :fullscreen="true">
      <div id="eyegaze_page" :class="{ 'text-dark' : isDark,'text-light' : !isDark }" :style="styleEyeGaze" :key="menuTrigger">

      <!-- Hero -->
      <section class="hero">
        <img src="/eyegazelogo_licensed1.jpg" alt="Two eyes flanking a glowing chess king" class="hero-image" />
        <div class="hero-text">
          <h1 class="hero-title">EyeGaze Chess</h1>
          <p class="hero-tagline">Chess doesn't stop. Neither do you.</p>
          <p class="hero-sub">Full chess — puzzles, computer play, and live opponents worldwide — controlled entirely with your eyes.</p>
          <ion-button expand="block" class="hero-cta" @click="handleStart">
            Start Playing
          </ion-button>
          <p class="hero-note">Works with any eye tracker that simulates a mouse click.</p>
        </div>
      </section>

      <!-- The problem -->
      <section class="feature-section">
        <div class="section-header">
          <h2 class="section-title">Chess was made for the mind.</h2>
        </div>
        <p class="section-intro">ALS and other conditions take so much. But they can't take the ability to think three moves ahead, to see a combination, to outplay an opponent. Chess belongs to the mind — and the mind doesn't stop.</p>
        <p class="section-intro">What stops is the ability to reach the board. To click the mouse. To tap the screen. That's the problem EyeGaze Chess solves.</p>
        <p class="section-intro">Henry Feldman has spent years making chess accessible — first with Verbal Chess, voice-controlled chess for blind and mobility-impaired players. EyeGaze Chess is the next step.</p>
      </section>


      <!-- The isolation problem -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap">
            <ion-icon :icon="globeOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">Stay Connected. Stay Competitive.</h2>
        </div>

        <div class="feature-card">
          <h3>Play Anyone in the World</h3>
          <p>EyeGaze Chess connects directly to Lichess.org — millions of players, rated games, real opponents around the clock. Not a simplified version. Not a special accessibility mode. The same Lichess everyone else plays.</p>
        </div>

        <div class="feature-card">
          <h3>Play the Computer — Anytime</h3>
          <p>At 3am when sleep won't come but the mind is sharp. Free players can play against Komodo at its lowest skill level. Subscribers unlock all skill levels across Stockfish, Komodo, and Maia. A game is always available, always waiting.</p>
        </div>

        <div class="feature-card">
          <h3>Solve Puzzles</h3>
          <p>Over 4.7 million tactical puzzles from the Lichess database. The same puzzles every chess player uses to sharpen their game. Rated, categorized, and tracked — because you're still a chess player.</p>
        </div>

      </section>

      <!-- The board problem solved -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap">
            <ion-icon :icon="eyeOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">The Board Problem — Solved.</h2>
        </div>

        <p class="section-intro">Eye gaze technology has two problems with chess boards. EyeGaze Chess solves both.</p>

        <div class="feature-card highlight">
          <div class="feature-card-label">Problem 1 — 64 small squares</div>
          <h3>No More Targeting Small Squares</h3>
          <p>Instead of clicking on the board, EyeGaze Chess shows your legal moves as large buttons beside the board. Big targets. Easy to dwell on. No fine motor control needed.</p>
        </div>

        <div class="feature-card highlight">
          <div class="feature-card-label">Problem 2 — Thinking triggers moves</div>
          <h3>Think Without Moving</h3>
          <p>With standard eye gaze chess, staring at the board to think about your move accidentally triggers clicks. EyeGaze Chess separates thinking from acting. Stare at the board as long as you want — nothing happens. When you're ready, look at the move buttons. That's when the game responds.</p>
        </div>

        <div class="feature-card highlight">
          <h3>The Adaptive Move System</h3>
          <p>Legal moves are shown as large buttons — never more than 8 at a time. A smart algorithm that accounts for the pieces that can move, and the squares they can move to. The system adapts to the position so every move is always reachable in at most 3 selections.</p>
        </div>

      </section>

      <!-- Designed for eye gaze -->
      <section class="feature-section">
        <div class="section-header">
          <div class="section-icon-wrap">
            <ion-icon :icon="accessibilityOutline" aria-hidden="true" />
          </div>
          <h2 class="section-title">Designed for Eye Gaze. Every Detail.</h2>
        </div>

        <div class="feature-card">
          <h3>Big Button Navigation</h3>
          <p>Every menu, every option, every control is built for large targets. The main menu is eight large tiles — easy to distinguish, easy to dwell on, making it easy to navigate without frustration.</p>
        </div>

        <div class="feature-card">
          <h3>Scrolling Without Gestures</h3>
          <p>Scrolling a list with eye gaze is notoriously difficult. EyeGaze Chess replaces scroll gestures with explicit Scroll Up and Scroll Down buttons — always visible, always large, always where you expect them.</p>
        </div>

        <div class="feature-card">
          <h3>Cancel and Done — Always Visible</h3>
          <p>Every dialog has large Cancel and Done buttons pinned at the top of the screen — never hidden, never scrolled away. You're always one dwell away from getting out.</p>
        </div>

        <div class="feature-card">
          <h3>Scalable Controls</h3>
          <p>Toolbar buttons scale up for monitor use — because eye gaze users are on monitors, not phones, and controls should match the screen.</p>
        </div>

        <div class="feature-card">
          <h3>Works With Your Eye Tracker</h3>
          <p>EyeGaze Chess works with any eye tracker that simulates a standard mouse click — including the Tobii PCEye and similar devices. If your tracker can click, it can play chess.</p>
        </div>
      </section>

      <!-- Powered by ChessVis -->
      <section class="powered-section">
        <p class="powered-text">Powered by</p>
        <p class="powered-brand">ChessVis</p>
        <p class="powered-sub">EyeGaze Chess is part of the ChessVis family — the same platform behind 4.7 million puzzles, visualization training, and data-driven opening preparation used by thousands of chess players worldwide.</p>
      </section>

      <!-- Subscription note -->
      <section class="feature-section">
        <div class="feature-card subscription-card">
          <h3>A Note on Subscriptions</h3>
          <p>EyeGaze Chess uses the ChessVis subscription for some features. We know the eye tracker itself is a significant investment. If subscription cost is a concern, please reach out — we don't want anything standing between you and the game.</p>
          <ion-button fill="outline" class="contact-btn" @click="handleContact">
            Get in Touch
          </ion-button>
        </div>
      </section>

      <!-- Final CTA -->
      <section class="final-cta-section">
        <h2 class="final-cta-title">Chess doesn't stop.</h2>
        <p class="final-cta-sub">Start playing now — no download, no account required to begin.</p>
        <ion-button expand="block" class="final-cta-btn" @click="handleStart">
          Start Playing
        </ion-button>
        <p class="final-cta-note">Works on any computer with an eye tracker. Optimized for monitor use.</p>
      </section>
    </div>
    </ion-content>
    <Toolbar :elements="toolbarElements" @toolbar-click="toolbarClick" />

  </ion-page>
</template>

<script setup lang="ts">
import {
    IonPage, IonHeader, IonToolbar, IonTitle,
    IonContent, IonIcon, IonButton,
  } from '@ionic/vue'
  import {
    globeOutline, eyeOutline, accessibilityOutline,
  } from 'ionicons/icons'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import Toolbar from '@/components/Toolbar.vue'
  import { scrollElement } from '@/composables/screenSize'
  import {buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight }  from '@/composables/screenSize'
  import { ref, watch , onMounted } from 'vue'
  import { t } from '@/composables/translateText.js'

  const emit = defineEmits(['next-item-click'])

  const connectionStore = useConnectionStore()

  const whichMenu = localStorage.getItem("whichMenu")
  const showBigButtons = ref(whichMenu == "buttons" || connectionStore.getShowBigButtons)

  const isDark = ref(connectionStore.getDarkmode != "" && connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)

  var toolbarAction = "buttons"

  const TOOLBAR_SCROLL_UP = 0
  const TOOLBAR_SCROLL_DOWN = 1
  const TOOLBAR_DONE = 2

  const toolbarElements = ref([
      { index : TOOLBAR_SCROLL_UP, label : t("Up") ,disabled : false},
      { index : TOOLBAR_SCROLL_DOWN, label : t("Down") ,disabled : false}
        ]) 
  if (whichMenu == "buttons")
    toolbarElements.value.push({ index : TOOLBAR_DONE, label : t("Done") , disabled : false})

  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] })

   const styleEyeGaze = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  updateWidthHeights()

  watch(() => windowSizeStore.getWindowSize, () => {
    //console.log('*********** window size changes detected')
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    //console.log("*** logoff new window size",windowSizeRef.value)
    menuTrigger.value++
    updateWidthHeights()
  })

  function updateWidthHeights() {
    const availableSpace = availableWidthHeight()
    const eyeGazePositionTop = availableSpace.topIndent
    const eyeGazePositionLeft = availableSpace.leftRightIndent + availableSpace.leftIndent
    const eyeGazeHeight = availableSpace.height - eyeGazePositionTop + availableSpace.topIndent
    const eyeGazeWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    styleEyeGaze.value.height = eyeGazeHeight + "px"
    styleEyeGaze.value.width = eyeGazeWidth + "px"
    styleEyeGaze.value.top = eyeGazePositionTop + "px"
    styleEyeGaze.value.left = eyeGazePositionLeft + "px"
  }

   function toolbarClick(arrayIndex) {

    switch (arrayIndex) {
      case TOOLBAR_DONE :
        emit("next-item-click","big_button_menu",t("Big Button Menu"),"buttons")
        return
        break
      case TOOLBAR_SCROLL_DOWN :
        scrollElement("eyegaze_page","down")
        return
        break
      case TOOLBAR_SCROLL_UP :
        scrollElement("eyegaze_page","up")
        return
        break
    }

    if (showState.value == SHOW_BUTTONS) {
      toolbarElements.value[0].label = t("Review all Subscription Options")
      showState.value = SHOW_BLURB
      return
    }

    switch (toolbarAction) {
      case "verify" :
        emit("next-item-click","verify_email","Verify Email Address","subscribe")
        break
      case "signin" :
        emit("next-item-click","signin","Signin","subscribe")
        break
      case "buttons" :
        toolbarElements.value[0].label = t("Return to the Description")
        showState.value = SHOW_BUTTONS
        break
    }
  }

  function handleStart() {
    emit("next-item-click","big_button_menu",t("Big Button Menu"),"buttons")
  }

  function handleContact() {
    window.location.href = 'mailto:henry@chessvis.com?subject=EyeGaze Chess'
  }

</script>

<style scoped>
ion-toolbar {
  --background: #0a0a1a;
  --color: #ffffff;
  --border-color: #1a1a3a;
}

ion-title {
  font-size: 17px;
  font-weight: 500;
}

ion-content {
  --background: #0a0a1a;
}

/* ── Hero ── */
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border-bottom: 0.5px solid #1a1a3a;
}

.hero-image {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  object-position: center;
}

.hero-text {
  padding: 28px 24px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-title {
  font-size: 30px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 10px;
  letter-spacing: -0.5px;
}

.hero-tagline {
  font-size: 18px;
  font-weight: 500;
  color: #a090e0;
  margin: 0 0 12px;
  line-height: 1.4;
  font-style: italic;
}

.hero-sub {
  font-size: 14px;
  color: #888890;
  line-height: 1.6;
  margin: 0 0 24px;
}

.hero-cta {
  --background: #5040a0;
  --background-activated: #3a2e7a;
  --color: #ffffff;
  --border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  height: 56px;
  width: 100%;
  margin-bottom: 12px;
}

.hero-note {
  font-size: 14px;
  color: #888890;
  margin: 0;
}

/* ── Feature sections ── */
.feature-section {
  padding: 28px 16px 12px;
  border-bottom: 0.5px solid #1a1a3a;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.section-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #2a1a5a;
  color: #a090e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  line-height: 1.3;
}

.section-intro {
  font-size: 14px;
  color: #888890;
  line-height: 1.7;
  margin: 0 0 14px;
}

/* ── Feature cards ── */
.feature-card {
  background: #121224;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 10px;
  border: 0.5px solid #1a1a3a;
}

.feature-card.highlight {
  border-color: #5040a0;
  background: #0d0d20;
}

.feature-card-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8070c0;
  margin-bottom: 6px;
}

.feature-card h3 {
  font-size: 15px;
  font-weight: 600;
  color: #e0e0f0;
  margin: 0 0 8px;
}

.feature-card p {
  font-size: 13px;
  color: #b0b0bc;
  line-height: 1.7;
  margin: 0;
}

/* ── Subscription card ── */
.subscription-card {
  border-color: #3a2a60;
  background: #0d0d20;
  text-align: center;
}

.subscription-card h3 {
  color: #a090e0;
}

.contact-btn {
  --color: #a090e0;
  --border-color: #5040a0;
  --border-radius: 10px;
  margin-top: 14px;
  font-size: 13px;
}

/* ── Powered by ── */
.powered-section {
  padding: 28px 20px;
  text-align: center;
  border-bottom: 0.5px solid #1a1a3a;
  background: #080814;
}

.powered-text {
  font-size: 11px;
  color: #444450;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 4px;
}

.powered-brand {
  font-size: 22px;
  font-weight: 700;
  color: #9FE1CB;
  margin: 0 0 10px;
}

.powered-sub {
  font-size: 13px;
  color: #555560;
  line-height: 1.6;
  margin: 0;
}

/* ── Final CTA ── */
.final-cta-section {
  padding: 36px 20px 48px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}

.final-cta-title {
  font-size: 26px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  font-style: italic;
}

.final-cta-sub {
  font-size: 14px;
  color: #888890;
  line-height: 1.6;
  margin: 0;
}

.final-cta-btn {
  --background: #5040a0;
  --background-activated: #3a2e7a;
  --color: #ffffff;
  --border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  height: 56px;
  width: 100%;
  margin-top: 8px;
}

.final-cta-note {
  font-size: 12px;
  color: #888890;
  margin: 0;
  line-height: 1.5;
}
</style>
